import React, { useState } from 'react';
import {
  TextField, Select, MenuItem, Button, InputLabel, FormControl,
  SelectChangeEvent, FormHelperText, Typography, Box,
} from '@mui/material';
import { Departement, Tiers } from '../../models/models';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from '../../components/SnackbarProvider';
import { createTransfert } from '../../reduxStore/ReglementSlice';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import TiersAutoC from '../AutoComplete/TiersAutoC';
import SelectDepartement from '../Selects/SelectDepartement';
import { getActionOptions } from './OperationDiverseForm';

/**
 * Transfert équilibré entre deux comptes tiers (sans caisse) : l'utilisateur
 * choisit l'opération sur le 1er tiers ; le 2nd reçoit automatiquement le sens
 * opposé (un débit, un crédit, même montant). Crée deux OD liées via /reglements/transfert.
 */
interface TransfertFormProps {
  onCancel: () => void;
}

const TransfertForm: React.FC<TransfertFormProps> = ({ onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);

  const [tiersA, setTiersA] = useState<Tiers | null>(null);
  const [tiersB, setTiersB] = useState<Tiers | null>(null);
  const [sensA, setSensA] = useState<'C' | 'D' | ''>('');
  const [montant, setMontant] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [departement, setDepartement] = useState<Departement | null>(selectedDepartement ?? null);

  // Transfert équilibré (double-entrée) : la contrepartie a TOUJOURS le sens brut
  // opposé (un Débit, un Crédit) → valeur nette des tiers inchangée, quels que
  // soient les types. Le facteur client/fournisseur ne change que l'affichage.
  const sensB: 'C' | 'D' | '' = sensA === 'C' ? 'D' : sensA === 'D' ? 'C' : '';
  const effetBLabel = tiersB && sensB
    ? getActionOptions(tiersB.type).find(o => o.value === sensB)?.label
    : null;

  const departementId = departement?.id ?? selectedDepartement?.id ?? null;

  const handleTiersAChange = (t: Tiers | null) => {
    setTiersA(t);
    setSensA(''); // l'action dépend du type du tiers → reset
  };

  const validate = (): string[] => {
    const m: string[] = [];
    if (!tiersA) m.push('Le premier tiers est obligatoire.');
    if (!tiersB) m.push('Le second tiers est obligatoire.');
    if (tiersA && tiersB && tiersA.id === tiersB.id) m.push('Les deux tiers doivent être différents.');
    if (!departementId) m.push('Le département est obligatoire.');
    if (!montant || Number(montant) <= 0) m.push('Le montant doit être strictement positif.');
    if (sensA !== 'C' && sensA !== 'D') m.push("L'opération sur le premier tiers est obligatoire.");
    return m;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const msgs = validate();
    if (msgs.length) { msgs.forEach(x => showSnackbar(x, 'error')); return; }
    try {
      await dispatch(createTransfert({
        tiers_a_id: tiersA!.id!,
        tiers_b_id: tiersB!.id!,
        sens_a: sensA as 'C' | 'D',
        montant: Number(montant),
        reference: reference || null,
        date_reglement: date,
        departement_id: departementId!,
      })).unwrap();
      showSnackbar('Transfert enregistré avec succès !', 'success');
      onCancel();
    } catch (err) {
      showSnackbar((err as string) || "Erreur lors de l'enregistrement", 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520, padding: 16 }}>
      <TextField
        label="Date"
        type="date"
        value={date}
        onChange={e => setDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required
        size="small"
      />

      {!selectedDepartement && (
        <SelectDepartement onDepartementChange={setDepartement} selectedDepartement={departement} />
      )}

      <TiersAutoC label="Premier tiers" onTiersChange={handleTiersAChange} value={tiersA} required />

      <FormControl fullWidth size="small" required disabled={!tiersA}>
        <InputLabel id="trf_action_label">Opération sur le premier tiers</InputLabel>
        <Select
          labelId="trf_action_label"
          value={sensA}
          onChange={(e: SelectChangeEvent) => setSensA(e.target.value as 'C' | 'D')}
          label="Opération sur le premier tiers"
          size="small"
        >
          {getActionOptions(tiersA?.type).map(o => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {tiersA ? "Choisissez l'opération." : "Sélectionnez d'abord le premier tiers."}
        </FormHelperText>
      </FormControl>

      <TiersAutoC label="Second tiers (contrepartie)" onTiersChange={setTiersB} value={tiersB} required />

      {effetBLabel && (
        <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            Contrepartie automatique sur le 2nd tiers : <strong>{effetBLabel}</strong>
          </Typography>
        </Box>
      )}

      <TextField
        label="Montant"
        type="number"
        value={montant}
        onChange={e => setMontant(e.target.value)}
        fullWidth
        required
        size="small"
      />

      <TextField
        label="Libellé / Référence"
        value={reference}
        onChange={e => setReference(e.target.value)}
        fullWidth
        size="small"
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="contained" color="primary" type="submit">Enregistrer le transfert</Button>
        <Button variant="outlined" color="inherit" type="button" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

export default TransfertForm;
